/**
 * Rental Booking Workers APIクライアント(Phase「LIFF frontend実装」)。
 *
 * 既存Workers route(rental-booking/src/index.ts)をそのまま利用する
 * (新規route追加なし)。すべてのリクエストへ`Authorization: Bearer <ID
 * token>`を付与し、`liff.getIDToken()`で取得したID tokenのみを本人確認情報
 * として送る(client側で組み立てたuser_id等は一切送らない)。
 *
 * 料金・空き判定・予約可否はこのクライアントでは一切計算・確定しない
 * (常にAPIレスポンスの値をそのまま表示・使用する)。
 */

import type {
  AdditionalPaymentIntentConfirmation,
  ApiErrorBody,
  ArrivalTimeChangeBody,
  ArrivalTimeChangeConfirmation,
  CancellationConfirmation,
  ChangeRequestBody,
  ChangeRequestConfirmation,
  CreateOnsiteBookingRequestBody,
  CreatePaymentIntentRequestBody,
  HoldStatusView,
  MyBookingsResponse,
  NewBookingConfirmation,
  NewBookingRequestBody,
  OnsiteBookingConfirmation,
  PaymentIntentConfirmation,
  UnavailableDatesResponse,
  ReceiptView,
  ReceiptDownloadTicket,
} from "./types.js";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly reason?: string,
  ) {
    super(`API error ${status}: ${code}${reason ? ` (${reason})` : ""}`);
    this.name = "ApiError";
  }
}

export interface RentalBookingApiClientOptions {
  baseUrl: string;
  /** 呼び出しのたびに最新のID tokenを取得する(長時間開いたままのタブでも失効しにくくする)。 */
  getIdToken: () => string | null;
  fetchImpl?: typeof fetch;
}

export class RentalBookingApiClient {
  private readonly baseUrl: string;
  private readonly getIdToken: () => string | null;
  private readonly fetchImpl: typeof fetch;

  constructor(options: RentalBookingApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.getIdToken = options.getIdToken;
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
  }

  private authHeaders(extra?: Record<string, string>): Record<string, string> {
    const idToken = this.getIdToken();
    if (!idToken) throw new Error("No LINE ID token available; call login() first");
    return { Authorization: `Bearer ${idToken}`, ...extra };
  }

  private async requestJson<T>(path: string, init: RequestInit): Promise<T> {
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, init);
    if (!response.ok) {
      let body: Partial<ApiErrorBody> = {};
      try {
        body = (await response.json()) as Partial<ApiErrorBody>;
      } catch {
        // エラーレスポンスがJSONでない場合はcodeを"unknown_error"扱いにする。
      }
      throw new ApiError(response.status, body.error ?? "unknown_error", body.reason);
    }
    return (await response.json()) as T;
  }

  async createBooking(body: NewBookingRequestBody): Promise<NewBookingConfirmation> {
    return this.requestJson<NewBookingConfirmation>("/v1/bookings", {
      method: "POST",
      headers: this.authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  }

  /**
   * Phase 11「新規予約｜予約済み日カレンダー不可表示」。fromDate/toDateは
   * "YYYY-MM-DD"(両端含む)。表示専用の事前案内であり、この結果を最終的な
   * 予約可否判定として使わない(送信時は既存どおりcreateBookingのサーバー
   * 側occupancy再判定・booking_conflictに従う)。
   *
   * excludeBookingIdは「変更申請カレンダーの新規予約との統一」で追加。
   * 変更申請中の予約自身を予約不可日として扱わせないために渡す
   * (new-booking画面からは渡さない)。
   */
  async getUnavailableDates(fromDate: string, toDate: string, excludeBookingId?: string): Promise<UnavailableDatesResponse> {
    const params = new URLSearchParams({ from: fromDate, to: toDate });
    if (excludeBookingId) params.set("exclude_booking_id", excludeBookingId);
    return this.requestJson<UnavailableDatesResponse>(`/v1/availability/dates?${params.toString()}`, {
      method: "GET",
      headers: this.authHeaders(),
    });
  }

  async getHoldStatus(useDate: string): Promise<HoldStatusView> {
    return this.requestJson<HoldStatusView>(`/v1/holds/${useDate}`, {
      method: "GET",
      headers: this.authHeaders(),
    });
  }

  async releaseHold(useDate: string, holdId: string): Promise<void> {
    await this.requestJson<{ ok: true }>(`/v1/holds/${useDate}/release`, {
      method: "POST",
      headers: this.authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ hold_id: holdId }),
    });
  }

  async createPaymentIntent(body: CreatePaymentIntentRequestBody): Promise<PaymentIntentConfirmation> {
    return this.requestJson<PaymentIntentConfirmation>("/v1/payment-intents", {
      method: "POST",
      headers: this.authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  }

  /** 現地払い（現金・PayPay） Step 2、2026-09-27追加指示。Stripeを通さない。 */
  async confirmOnsiteBooking(body: CreateOnsiteBookingRequestBody): Promise<OnsiteBookingConfirmation> {
    return this.requestJson<OnsiteBookingConfirmation>("/v1/bookings/onsite-payment", {
      method: "POST",
      headers: this.authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  }

  async getMyBookings(): Promise<MyBookingsResponse> {
    return this.requestJson<MyBookingsResponse>("/v1/bookings/mine", {
      method: "GET",
      headers: this.authHeaders(),
    });
  }

  async submitChangeRequest(bookingId: string, body: ChangeRequestBody): Promise<ChangeRequestConfirmation> {
    return this.requestJson<ChangeRequestConfirmation>(`/v1/bookings/${bookingId}/change-requests`, {
      method: "POST",
      headers: this.authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  }

  /** 来館時刻のみの変更(土日祝)。管理者承認なしで即時に確定する。 */
  async changeArrivalTime(bookingId: string, body: ArrivalTimeChangeBody): Promise<ArrivalTimeChangeConfirmation> {
    return this.requestJson<ArrivalTimeChangeConfirmation>(`/v1/bookings/${bookingId}/arrival-time`, {
      method: "POST",
      headers: this.authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  }

  /** 利用履歴を本人が非表示にする(予約・決済・領収書は削除されない)。 */
  async hideUsageHistory(bookingId: string): Promise<{ booking_id: string; hidden: true }> {
    return this.requestJson<{ booking_id: string; hidden: true }>(`/v1/bookings/${bookingId}/usage-history/hide`, {
      method: "POST",
      headers: this.authHeaders(),
    });
  }

  async createAdditionalPaymentIntent(bookingId: string): Promise<AdditionalPaymentIntentConfirmation> {
    return this.requestJson<AdditionalPaymentIntentConfirmation>(
      `/v1/bookings/${bookingId}/change-requests/additional-payment-intent`,
      { method: "POST", headers: this.authHeaders() },
    );
  }

  async cancelBooking(bookingId: string): Promise<CancellationConfirmation> {
    return this.requestJson<CancellationConfirmation>(`/v1/bookings/${bookingId}/cancel`, {
      method: "POST",
      headers: this.authHeaders(),
    });
  }

  /**
   * 返金明細PDFを取得する。認証付きdownload endpoint(rental-booking/src/http/
   * handlers/refund-statement-download.ts)はPDFバイト列を直接返すため、
   * JSONではなくBlobとして扱う。呼び出し側(screens/my-bookings.ts)は
   * object URLを生成してユーザーへ提示する(生のURLをUIへ埋め込まず、
   * Bearer tokenが必要なfetch経由でのみ取得する)。
   */
  async fetchRefundStatementPdf(pathOrBookingId: string): Promise<Blob> {
    // refund_statement_pdf_urlはbackendが返す絶対path("/v1/bookings/{id}/refund-statement")
    // のことが多いが、念のためbooking_idだけが渡された場合にも対応する。
    const path = pathOrBookingId.startsWith("/") ? pathOrBookingId : `/v1/bookings/${pathOrBookingId}/refund-statement`;
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: "GET",
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      let body: Partial<ApiErrorBody> = {};
      try {
        body = (await response.json()) as Partial<ApiErrorBody>;
      } catch {
        // PDF成功時はJSONではないため、失敗時のみJSONを試みる。
      }
      throw new ApiError(response.status, body.error ?? "unknown_error", body.reason);
    }
    return response.blob();
  }

  /**
   * 領収書PDFを取得する(Phase 11 Step 11-1「領収書PDF BLOCKER解消」)。
   * fetchRefundStatementPdfと全く同じ設計方針(認証付きdownload endpoint
   * <rental-booking/src/http/handlers/receipt-download.ts>からBlobとして
   * 取得、生のURLをUIへ埋め込まない)。
   */
  async fetchReceiptPdf(pathOrBookingId: string): Promise<Blob> {
    // receipt_pdf_urlはbackendが返す絶対path("/v1/bookings/{id}/receipt")
    // のことが多いが、念のためbooking_idだけが渡された場合にも対応する。
    const path = pathOrBookingId.startsWith("/") ? pathOrBookingId : `/v1/bookings/${pathOrBookingId}/receipt`;
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: "GET",
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      let body: Partial<ApiErrorBody> = {};
      try {
        body = (await response.json()) as Partial<ApiErrorBody>;
      } catch {
        // PDF成功時はJSONではないため、失敗時のみJSONを試みる。
      }
      throw new ApiError(response.status, body.error ?? "unknown_error", body.reason);
    }
    return response.blob();
  }

  async getReceiptView(bookingId: string): Promise<ReceiptView> {
    return this.requestJson<ReceiptView>(`/v1/bookings/${bookingId}/receipt/view`, {
      method: "GET",
      headers: this.authHeaders(),
    });
  }

  async createReceiptDownloadTicket(bookingId: string): Promise<ReceiptDownloadTicket> {
    return this.requestJson<ReceiptDownloadTicket>(`/v1/bookings/${bookingId}/receipt/download-ticket`, {
      method: "POST",
      headers: this.authHeaders(),
    });
  }
}
